/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"
import { Box, useTheme } from "@mui/material"
import { featureCollection } from "@turf/helpers"
import _, { clamp, get, groupBy } from "lodash"

import config from "../config"

import { useVenue } from "providers/venue"
import { useRoute } from "providers/route"
import { useGeoLocation } from "providers/geolocation"
import { useAppUI } from "../providers/ui"
import { useVenueController } from "hooks/venue/useVenueController"

import {
  IndoorMap,
  getSuitablyValueBetweenBearings,
  getLocationIdByFeature,
} from "components/IndoorMap"

const FALLBACK_CENTER = [100.5017051, 13.7572619]
const DEFAULT_ZOOM = 18.5
const DEFAULT_MIN_ZOOM = null
const DEFAULT_MAX_ZOOM = 22
const DEFAULT_MOBILE_PIXEL_RATIO = 1
const DEFAULT_KIOSK_PIXEL_RATIO = 2

export const IndoorMapContainer = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    isLoading,
    userLocation,
    scannedLocations,
    deviceLocation,
    locationVenue,
  } = useGeoLocation()

  const {
    venues,
    features,
    amenities,
    occupants,
    groupedLocationBySection,
    labels,
    decorations,
    findFeatureById,
    dataLoaded,
    transformLocationToFeature,
    transformStepsToGeometries,
    relationshipGraphLoaded,
    mapTheme,
    mapConfig,
    defaultVenue,
  } = useVenue()

  const locationVenueId = locationVenue?.id || defaultVenue?.id
  const { viewingVenue: viewingVenueId } = useVenueController(locationVenueId)
  const isAutoFillOrigin = useRef(false)
  const { mapReady, setmapReady, startMapLoading, stopMapLoading } = useAppUI()
  const { route } = useRoute()
  const indoorRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapLayout, setMapLayout] = useState("full")
  const theme = useTheme()
  const styleOverrides = theme.components[`AppMap`]?.styleOverrides || {}
  const { indoorMapContainer } = styleOverrides

  const {
    origin: originParams,
    destination: destinationParams,
    step,
    ordinal,
  } = useParams()
  const [searchParams] = useSearchParams()

  const defaultCenter = useMemo(
    () =>
      _.get(venues, "[0].properties.display_point.coordinates") ||
      FALLBACK_CENTER,
    [venues]
  )

  const defaultBearing = useMemo(
    () => _.get(mapConfig, "default_bearing", 0),
    [mapConfig]
  )
  const defaultPitch = useMemo(
    () => _.get(mapConfig, "default_pitch", 45),
    [mapConfig]
  )

  const zoomOptions = useMemo(() => {
    const minZoom = _.get(mapConfig, "min_zoom", DEFAULT_MIN_ZOOM)
    const maxZoom = _.get(mapConfig, "max_zoom", DEFAULT_MAX_ZOOM)
    return minZoom < maxZoom
      ? { minZoom, maxZoom }
      : { minZoom: DEFAULT_MIN_ZOOM, maxZoom: DEFAULT_MAX_ZOOM }
  }, [mapConfig])

  const minZoom = useMemo(() => _.get(zoomOptions, "minZoom"), [zoomOptions])

  const maxZoom = useMemo(() => _.get(zoomOptions, "maxZoom"), [zoomOptions])

  const targetOrigin = useMemo(
    () => transformLocationToFeature(originParams),
    [originParams, transformLocationToFeature]
  )

  const targetDestination = useMemo(
    () => transformLocationToFeature(destinationParams),
    [destinationParams, transformLocationToFeature]
  )
  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const pixelRatio = useMemo(
    () =>
      isKioskMode
        ? _.get(mapConfig, "kiosk_pixel_ratio", DEFAULT_KIOSK_PIXEL_RATIO)
        : _.get(mapConfig, "mobile_pixel_ratio", DEFAULT_MOBILE_PIXEL_RATIO),
    [mapConfig]
  )

  const getAmenityIdsFromUnitIds = useCallback(
    (unitIds) => {
      const matchingAmenities = amenities.filter((amenity) =>
        amenity.properties?.unit_ids.some((id) => unitIds.includes(id))
      )
      return matchingAmenities.map((amenity) => amenity.id)
    },
    [amenities]
  )

  useEffect(() => {
    /* Handle User location
     * @Eample: /maps/ordinal/2?user-location=98.29877730421258,7.892412617738349,2o
     */
    if (!indoorRef.current) return

    if (mapLoaded && isKioskMode && deviceLocation) {
      indoorRef.current?.addUserLocation(deviceLocation)
    } else if (mapLoaded && userLocation) {
      indoorRef.current?.addUserLocation(userLocation)
    } else {
      indoorRef.current?.removeUserLocation()
    }
  }, [userLocation, mapLoaded, deviceLocation, indoorRef.current])

  useEffect(() => {
    /**
     * Handle last scanned locations
     */
    if (!indoorRef.current) return

    //TODO:: make this logic supports multiple locations?
    const scannedLocation = _.last(scannedLocations)
    if (mapReady && scannedLocation) {
      indoorRef.current?.removeLastUserLocation()
      indoorRef.current?.addLastUserLocation(scannedLocation)
    } else {
      indoorRef.current?.removeLastUserLocation()
    }
  }, [scannedLocations, mapReady, deviceLocation, indoorRef.current])

  useEffect(() => {
    if (
      !indoorRef.current ||
      !mapLoaded ||
      !dataLoaded ||
      !mapTheme ||
      !relationshipGraphLoaded
    )
      return

    if (location.pathname !== "/" && location.pathname.indexOf("/maps") !== 0) {
      // Hide Map
      setMapLayout("hidden")
      return
    }

    const [action, value] = location.pathname.replace("/maps/", "").split("/")

    setMapLayout("full")

    // Handle the map's direction and the map's interactive
    switch (action) {
      case "dir":
        indoorRef.current.disableClick(true)
        indoorRef.current.hideUserLocationMarker()
        indoorRef.current.hideLastUserLocationMarker()
        break
      case "":
        isAutoFillOrigin.current = false
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.showUserLocationMarker()
        indoorRef.current.disableClick()
        break
      default:
        isAutoFillOrigin.current = false
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.showUserLocationMarker()
        indoorRef.current.enableClick()
        indoorRef.current.setFeatureObject3DsOpacity(1)
        break
    }

    // Handle the map's rendering
    switch (action) {
      case "place": {
        indoorRef.current.showVenueObjects()
        const feature = findFeatureById(value)
        const element_id = getLocationIdByFeature(feature)
        let relatedLocationIds = []
        const isSection = feature?.feature_type === "section"
        if (isSection) {
          relatedLocationIds = _.get(groupedLocationBySection, value, []).map(
            (location) => location?.id
          )
        }
        if (element_id) {
          indoorRef.current.changeLevelByOrdinal(
            Number(feature?.properties?.ordinal || 0)
          )
          indoorRef.current.clearHighlightElements()
          indoorRef.current.clearHighlightObject()

          const highlightElementIds = _.compact(
            _.uniq([...relatedLocationIds, element_id, value])
          )

          // !TEMP: Temporary workaround to get related element for getFeatureExtent without highlighting units under the section
          const highlightedFeature = isSection
            ? indoorRef.current.getElementByIds(highlightElementIds)
            : indoorRef.current.setHighlightElementIds(highlightElementIds)

          // Handle Fly to animation
          try {
            const extent =
              indoorRef.current.getFeatureExtent(highlightedFeature)
            const center = indoorRef.current.getExtentCenter(extent)
            const zoom = _.clamp(
              indoorRef.current.getExtentZoom(extent),
              19,
              22
            )
            const targetView = {
              center,
              zoom,
            }
            let targetCenter = center

            const baseOffset = {
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }
            const adjustedCenterWithOffset =
              indoorRef.current.getTargetViewCenter(targetView, {
                offset: {
                  ...baseOffset,
                  ...(isKioskMode
                    ? { right: isSection ? 500 : 0 }
                    : { bottom: 200 }),
                },
              })
            targetCenter = adjustedCenterWithOffset

            indoorRef.current.flyTo(targetCenter, { zoom, pitch: 40 })
          } catch (error) {
            console.warn("Can not highligh the given elements")
          }
        }
        break
      }

      case "ordinal": {
        indoorRef.current.showVenueObjects()
        const currentOrdinal = Number(ordinal)
        const venue = findFeatureById(viewingVenueId)

        const venueDisplayCoordinates = _.get(
          venue,
          "properties.display_point.coordinates"
        )

        const venueDefaultZoom = _.get(venue, "properties.zoom")
        indoorRef.current.changeLevelByOrdinal(currentOrdinal)
        if (
          userLocation &&
          currentOrdinal === userLocation?.properties?.ordinal &&
          viewingVenueId === locationVenue?.id
        ) {
          const currentBearing = indoorRef.current.getBearing()
          const bearing = getSuitablyValueBetweenBearings(
            defaultBearing,
            currentBearing
          )
          const pitch = defaultPitch
          const locationExtent =
            indoorRef.current.getFeatureExtent(userLocation)
          const venueExtent = indoorRef.current.getFeatureExtent(locationVenue)
          const center = indoorRef.current.getExtentCenter(locationExtent)
          const zoom = clamp(
            venueDefaultZoom ||
              indoorRef.current.getExtentZoom(venueExtent) + 1,
            19,
            22
          ) //adjust offset

          indoorRef.current.flyTo(center, {
            zoom,
            bearing,
            pitch,
          })
        } else if (venue && venue.feature_type === "venue") {
          const currentBearing = indoorRef.current.getBearing()
          const bearing = getSuitablyValueBetweenBearings(
            defaultBearing,
            currentBearing
          )
          const pitch = defaultPitch
          const extent = indoorRef.current.getFeatureExtent(venue)
          const center =
            venueDisplayCoordinates || indoorRef.current.getExtentCenter(extent)
          const zoom = clamp(
            venueDefaultZoom || indoorRef.current.getExtentZoom(extent) + 1,
            19,
            22
          ) //adjust offset
          indoorRef.current.flyTo(center, {
            zoom,
            bearing,
            pitch,
          })
        }
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
      case "dir": {
        indoorRef.current.hideVenueObjects()
        if (
          !isAutoFillOrigin.current &&
          !targetOrigin &&
          targetDestination &&
          userLocation
        ) {
          isAutoFillOrigin.current = true
          navigate(
            `/maps/dir/${userLocation.properties.userLocationParam}/${targetDestination?.id}`,
            { replace: true }
          )
          return
        }

        if (
          isKioskMode &&
          !targetOrigin &&
          targetDestination &&
          deviceLocation
        ) {
          navigate(
            `/maps/dir/${deviceLocation.properties.userLocationParam}/${targetDestination?.id}`,
            { replace: true }
          )
          return
        }

        if (!targetOrigin || !targetDestination) {
          // Clear path in the route path Layer when the origin or destination input was empty
          indoorRef.current.clearNavigationGeometries()

          if (!targetOrigin && !targetDestination) {
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()
            return
          }

          // Add highlight to the origin or detination element (if exist)
          const targetFeature = targetOrigin || targetDestination
          const element_id = getLocationIdByFeature(
            targetOrigin || targetDestination
          )
          if (element_id) {
            indoorRef.current.changeLevelByOrdinal(
              Number(targetFeature?.properties?.ordinal || 0)
            )
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()
            const highlightedFeature = indoorRef.current.setHighlightElementIds(
              [element_id]
            )
            // Handle Fly to animation
            const extent =
              indoorRef.current.getFeatureExtent(highlightedFeature)
            const center = indoorRef.current.getExtentCenter(extent)
            const zoom = indoorRef.current.getExtentZoom(extent)
            indoorRef.current.flyTo(center, { zoom })
            indoorRef.current.setHighlightedObject([element_id])
          }
          return
        }

        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        if (!route) return

        const navigationGeometries = transformStepsToGeometries(route.steps)
        const viewingOrdinal = Number(
          searchParams.get("ordinal") || route.origin.properties.ordinal
        )

        //Clear path in the route path Layer
        indoorRef.current.clearNavigationGeometries()

        // Create route path on map
        indoorRef.current.createNavigationGeometries(navigationGeometries)

        //create current overview step path by ordinal
        const path = indoorRef.current.createOverviewStepPathByOrdinal(
          navigationGeometries,
          viewingOrdinal
        )

        if (!step) {
          //Change level on map by ordinal
          indoorRef.current.changeLevelByOrdinal(viewingOrdinal)
          // animate fly to and display overview step path by current ordinal
          indoorRef.current.flyToOverviewStepPath(path)
          return
        }

        if (step) {
          // handle the step page
          const stepNumber = parseInt(step)
          const navigationGeometries = transformStepsToGeometries(route.steps)
          const targetStep = navigationGeometries.find(
            ({ id }) => id === `step-${stepNumber}`
          )
          if (!targetStep) return
          indoorRef.current.clearHighlightObject()
          const { id, properties, isVerticalStep, isLastStep, geometry } =
            targetStep
          const { ordinal, intermediaryUnits } = properties
          //only show geometry, path and maker in current ordinal
          indoorRef.current.changeLevelByOrdinal(ordinal)
          if (isVerticalStep) {
            //verticalStep's intermediaries are elevators/escalators/stairs
            const amenitiesToHighlight =
              getAmenityIdsFromUnitIds(intermediaryUnits)
            //highlight elevators/escalators/stairs markers
            indoorRef.current.setHighlightedObject(amenitiesToHighlight)
            break
          }
          //If this is vertical step or last step will animate fly to and zooming in on the geometry.
          if (isVerticalStep || isLastStep) {
            indoorRef.current.flyToAndZoomIn(geometry.coordinates)
            break
          }
          // animate fly to and display the pathfly to and display the path
          const targetGeometryInLayer =
            indoorRef.current.getRoutePathGeometryById(id)
          if (!targetGeometryInLayer) break
          indoorRef.current.flyToPath(targetGeometryInLayer)
          //set opacity of extrudedUnits to 0.5
          indoorRef.current.setFeatureObject3DsOpacity(0.5)
        }

        // hide origin and destination logo / text marker
        indoorRef.current.hideGeometryByElementId(route.origin.id)
        indoorRef.current.hideGeometryByElementId(route.destination.id)
        break
      }
      case "occupants": {
        indoorRef.current.hideVenueObjects()
        const viewingVenue = findFeatureById(viewingVenueId)

        const viewingOrdinalQuery = searchParams.get("ordinal")
        const currentFloorOrdinal = Number(viewingOrdinalQuery)

        if (!_.isEmpty(viewingOrdinalQuery)) {
          const amenitiesByCategoryByVenue = occupants.filter(
            (occupant) =>
              occupant.properties.category === value &&
              occupant.properties.venue.id === viewingVenue?.id
          )

          const filteredAmenitiesOnOrdinal = amenitiesByCategoryByVenue.filter(
            (occupant) => occupant.properties.ordinal === currentFloorOrdinal
          )
          const filteredAmenitiesIds = filteredAmenitiesOnOrdinal.map(
            (occupant) => occupant.id
          )

          try {
            indoorRef.current.changeLevelByOrdinal(currentFloorOrdinal)
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()

            const highlightedFeatures =
              indoorRef.current.setHighlightElementIds(filteredAmenitiesIds)
            indoorRef.current.setHighlightedObject(filteredAmenitiesIds)

            if (highlightedFeatures) {
              // Handle Fly to animation
              const zoomOffset = filteredAmenitiesIds.length > 1 ? -0.1 : -3
              const extent =
                indoorRef.current.getFeatureExtent(highlightedFeatures)
              const center = indoorRef.current.getExtentCenter(extent)
              const zoom = indoorRef.current.getExtentZoom(extent)
              indoorRef.current.flyTo(center, {
                zoom: zoom - zoomOffset,
              })
            } else {
              const currentBearing = indoorRef.current.getBearing()
              const bearing = getSuitablyValueBetweenBearings(
                defaultBearing,
                currentBearing
              )
              indoorRef.current.flyTo(defaultCenter, {
                zoom: DEFAULT_ZOOM,
                bearing,
                pitch: 45,
              })
            }
          } catch (error) {
            console.log(error)
          }
        }
        break
      }
      case "amenities": {
        indoorRef.current.hideVenueObjects()
        const viewingVenue = findFeatureById(viewingVenueId)

        const viewingOrdinalQuery = searchParams.get("ordinal")
        const currentFloorOrdinal = Number(viewingOrdinalQuery)

        if (!_.isEmpty(viewingOrdinalQuery)) {
          const amenitiesByCategoryByVenue = amenities.filter(
            (amenity) =>
              amenity.properties.category === value &&
              amenity.properties.venue.id === viewingVenue?.id
          )

          const filteredAmenitiesOnOrdinal = amenitiesByCategoryByVenue.filter(
            (amenity) => amenity.properties.ordinal === currentFloorOrdinal
          )
          const filteredAmenitiesIds = filteredAmenitiesOnOrdinal.map(
            (amenity) => amenity.id
          )

          try {
            indoorRef.current.changeLevelByOrdinal(currentFloorOrdinal)
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()

            const highlightedFeatures =
              indoorRef.current.setHighlightElementIds(filteredAmenitiesIds)
            indoorRef.current.setHighlightedObject(filteredAmenitiesIds)

            if (highlightedFeatures) {
              // Handle Fly to animation
              const zoomOffset = filteredAmenitiesIds.length > 1 ? -0.1 : -3
              const extent =
                indoorRef.current.getFeatureExtent(highlightedFeatures)
              const center = indoorRef.current.getExtentCenter(extent)
              const zoom = indoorRef.current.getExtentZoom(extent)
              indoorRef.current.flyTo(center, {
                zoom: zoom - zoomOffset,
              })
            } else {
              const currentBearing = indoorRef.current.getBearing()
              const bearing = getSuitablyValueBetweenBearings(
                defaultBearing,
                currentBearing
              )
              indoorRef.current.flyTo(defaultCenter, {
                zoom: DEFAULT_ZOOM,
                bearing,
                pitch: 45,
              })
            }
          } catch (error) {
            console.log(error)
          }
        }
        break
      }
      case "": {
        indoorRef.current.showVenueObjects()
        setMapLayout("bottom-half")
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(
          defaultBearing,
          currentBearing
        )
        const venuesCollection = featureCollection(venues)
        const extent = indoorRef.current.getFeatureExtent(venuesCollection)
        const center = indoorRef.current.getExtentCenter(extent)
        const zoom = indoorRef.current.getExtentZoom(extent)
        indoorRef.current.flyTo(center, {
          zoom,
          bearing,
        })
        indoorRef.current.changeLevelByOrdinal(0)
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
      default: {
        indoorRef.current.showVenueObjects()
        indoorRef.current.changeLevelByOrdinal(0)
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(0, currentBearing)
        indoorRef.current.flyTo(defaultCenter, {
          zoom: DEFAULT_ZOOM,
          bearing,
        })
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
    }
  }, [
    indoorRef.current,
    location.pathname,
    findFeatureById,
    navigate,
    isLoading,
    userLocation,
    relationshipGraphLoaded,
    searchParams,
    mapLoaded,
    groupedLocationBySection,
    route,
    viewingVenueId,
  ])

  const handleClickElement = useCallback(
    (params) => {
      const replace = window.location.pathname.startsWith("/maps/place")
      const { id, feature_type, category } = params.target.properties
      if (feature_type === "section") {
        return navigate(`/maps/place/${id}`, { replace })
      }

      switch (`${feature_type}-${category}`) {
        case "unit-room":
        case "unit-unenclosedarea":
        case "unit-recreation":
        case "kiosk-kiosk":
          // Check if the clicked unit/kiosk has multiple bound occupants and a section; if so, navigate to the section's PlaceDetail page
          const groupedOccupantsByLocation = groupBy(occupants, (occupant) => {
            const unitId = get(occupant, "properties.unit_id")
            const kioskId = get(occupant, "properties.kiosk_id")
            return unitId || kioskId
          })
          const hasMultipleOccupants =
            get(groupedOccupantsByLocation, id, []).length > 1
          const occupant = features.find((f) => {
            return (
              f.properties.anchor?.properties?.unit_id === id ||
              f.properties?.kiosk_id === id
            )
          })
          const occupantId = occupant?.id
          const sectionId = occupant?.properties?.section?.id
          const placeId =
            hasMultipleOccupants && sectionId ? sectionId : occupantId
          if (placeId) navigate(`/maps/place/${placeId}`, { replace })
          break
        case "amenity-atm":
        case "amenity-babychanging":
        case "amenity-strollerrental":
        case "amenity-boardinggate.ferry":
        case "amenity-elevator":
        case "amenity-escalator":
        case "amenity-stairs":
        case "amenity-information":
        case "amenity-wheelchair":
        case "amenity-restroom":
        case "amenity-restroom.male":
        case "amenity-restroom.female":
        case "amenity-restroom.wheelchair":
        case "amenity-taxi":
        case "amenity-bus":
        case "amenity-parking":
        case "amenity-parking.bicycle":
        case "amenity-parking.motorcycle":
        case "amenity-privatelounge":
        case "amenity-landmark":
        case "amenity-rail.muni":
        case "amenity-service":
        case "amenity-smokingarea":
        case "amenity-ticketing":
        case "amenity-meetingpoint":
        case "amenity-prayerroom":
        case "amenity-firstaid":
        case "amenity-ticketing.rail":
        case "occupant-currencyexchange":
          navigate(`/maps/place/${id}`, { replace })
          break
        default:
          break
      }
    },
    /** Remove navigate because it trigger unneccessary re-render & createFeatures in Indoormap */
    [features, occupants]
    /* eslint-enable react-hooks/exhaustive-deps */
  )

  useEffect(() => {
    const indoorMap = new IndoorMap("map", {
      defaultCenter,
      defaultZoom: DEFAULT_ZOOM,
      defaultBearing,
      mapTheme,
      onMapLoading: () => {
        startMapLoading()
      },
      onMapReady: (e) => {
        setMapLoaded(true)
        stopMapLoading()
        setmapReady(true)
        // TODO : Replace with hide all geometry later
        indoorRef?.current?.changeLevelByOrdinal(0)
      },
      mapConfig: mapConfig,
      minZoom,
      maxZoom,
      // The pixel ratio should be later moved to app config
      pixelRatio,
    })
    indoorRef.current = indoorMap
    return () => {
      indoorRef.current = null
      indoorMap.map.remove()
    }
  }, [
    pixelRatio,
    defaultCenter,
    defaultBearing,
    mapTheme,
    setmapReady,
    startMapLoading,
    stopMapLoading,
    mapConfig,
    minZoom,
    maxZoom,
  ])

  /** Init with features  */
  useEffect(() => {
    if (
      indoorRef.current &&
      // !indoorRef.current.elementsLoaded &&
      mapTheme &&
      dataLoaded
    ) {
      indoorRef.current.onClickElement = handleClickElement
      indoorRef.current.mapDecorations = decorations
      indoorRef.current.features = features
      indoorRef.current.groundLabels = labels
    }
  }, [
    indoorRef,
    features,
    mapTheme,
    labels,
    decorations,
    dataLoaded,
    handleClickElement,
  ])

  return (
    <Box sx={indoorMapContainer} className={mapLayout}>
      <div id="map"></div>
    </Box>
  )
}
